import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, AutoComplete, Button, Drawer, Empty, Input, Modal, Select, Switch, Table,
  Tabs, Tag, Tooltip, message,
} from "antd";
import {
  ReloadOutlined, EditOutlined, HistoryOutlined, WarningOutlined,
  RedoOutlined, BulbOutlined, PlusOutlined, KeyOutlined, ProfileOutlined,
  ApartmentOutlined, DeleteOutlined, TranslationOutlined, NodeIndexOutlined,
  MessageOutlined, InfoCircleOutlined,
} from "@ant-design/icons";

import DashboardLayout from "../../layout/dashboardLayout/DashboardLayout";
import RequestsPanel from "./RequestsPanel";
import { controller } from "../../controller";
import {
  getCarrierKnowledgeOverview,
  getCarrierPlaybook,
  getCarrierPlaybookHistory,
  saveCarrierPlaybook,
  revertCarrierPlaybook,
  getCarrierKnowledgeReports,
  retryCarrierReport,
  getOfficesList,
  getCarrierLexicon,
  saveLexiconTerm,
  deleteLexiconTerm,
} from "./controller";
import {
  popupInDrawer, CredentialsTab, ProcedureCodesTab, CarrierRecordTab,
} from "./CarrierDrawerTabs";
import { CarrierNamesTab, UnresolvedPmsCarriersPanel } from "./CarrierNamesPanels";
import "./crm.css";
import "./carrierKnowledge.css";

/**
 * Carrier Knowledge — the whole playbook set on one screen. Super admin only.
 *
 * The playbooks shipped with no management surface: they could be read one
 * carrier-and-task at a time in the insurance modal and changed only by filing a
 * report. The history and revert endpoints existed and were reachable from
 * nothing — which mattered, because "full history, one-click revert" was the
 * argument for letting a single report auto-publish to everyone.
 *
 * The default sort puts UNWRITTEN carriers first. Most of them are unwritten, and
 * a management page that hides its own gaps is not managing anything.
 *
 * It has since absorbed everything else that is per-carrier: the portal logins
 * (previously one office page at a time), the procedure-code defaults and the
 * carrier record itself (previously /office/carriers, now retired into this).
 * See CarrierDrawerTabs — none of it is reimplemented, only rearranged.
 */

const TASKS = [
  { key: "breakdown", label: "Breakdown" },
  { key: "estimate", label: "Estimate" },
  { key: "general", label: "General" },
];

const AVAILABILITY = [
  { value: "unknown", label: "Not established yet" },
  { value: "available", label: "Can be done" },
  { value: "partial", label: "Only partly — see limitations" },
  { value: "unavailable", label: "No known route" },
];

const AVAIL_COLOUR = { available: "green", partial: "orange", unavailable: "red" };

function when(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch (e) {
    return "—";
  }
}

/** One task's cell in the overview table: the state at a glance, or the gap. */
function TaskCell({ info }) {
  if (!info) return <span className="ck-blank">not written</span>;
  return (
    <span className="ck-taskcell">
      <Tag color={AVAIL_COLOUR[info.availability]} className="ck-tag">
        v{info.version}
      </Tag>
      {info.has_ivr ? <Tooltip title="Has a phone menu path"><span className="ck-dot">☎</span></Tooltip> : null}
      {info.has_limitations ? <Tooltip title="Records what it won't give you"><span className="ck-dot">⚠</span></Tooltip> : null}
      {info.confirmations ? <span className="ck-conf">✓{info.confirmations}</span> : null}
    </span>
  );
}

// --- the editor -------------------------------------------------------------

// Mirrors clinics/services/carrier_playbooks.py. Anything not in these lists is
// dropped server-side, so keeping them in step is what stops the editor offering
// a value that silently vanishes on save.
const GRANULARITY = [
  { value: "", label: "Not established yet" },
  { value: "per_code", label: "Per procedure code" },
  { value: "per_name", label: "Procedure names only, no codes" },
  { value: "per_category", label: "Per category only" },
  { value: "mixed", label: "Mixed — depends who you get" },
];

const QUOTES_CODES = [
  { value: "", label: "Not established yet" },
  { value: "yes", label: "Yes, they quote codes" },
  { value: "no", label: "No, and not even if asked" },
  { value: "sometimes", label: "Sometimes" },
];

const FREQUENCY_BASIS = [
  { value: "", label: "Not established yet" },
  { value: "calendar_year", label: "Calendar year" },
  { value: "rolling_12m", label: "Rolling from last service date" },
  { value: "benefit_year", label: "Plan's benefit year" },
  { value: "varies", label: "Varies by plan — ask" },
];

const BENEFIT_YEAR_START = [
  { value: "", label: "Not established yet" },
  { value: "calendar", label: "January" },
  { value: "anniversary", label: "Policy anniversary" },
  { value: "varies", label: "Varies by plan" },
];

const RULE_TYPES = [
  "frequency_basis", "shared_maximum", "shared_frequency", "age_limit",
  "waiting_period", "maximum_reset", "downgrade", "missing_tooth",
  "coordination_of_benefits", "deductible_application", "pre_auth_threshold",
  "fee_guide", "exclusion", "other",
];

// Suggestions only — a lexicon target may also be a bare CDA code, so these are
// offered by an AutoComplete rather than enforced by a Select.
const COVERAGE_CATEGORIES = ["Preventative", "Basic", "Major", "Orthodontics"];

/**
 * Lift a Drawer tooltip above the Drawer.
 *
 * The sixth time this codebase has hit an antd popup losing to the Drawer. A
 * Tooltip portals to document.body at z-index 1200; this app's Drawer sits at
 * 10001, so the tooltip renders and is "visible" to a test while being painted
 * underneath — hovering appears to do nothing at all.
 *
 * z-index rather than `popupInDrawer` here: the drawer body scrolls, so
 * containing the tooltip would clip it against the edges of a form that is
 * mostly full-width fields. A transient overlay wants to escape, just above.
 */
const DRAWER_TOOLTIP_Z = 10050;

/**
 * A field label with an explanation a hover away.
 *
 * The labels are necessarily terse, and the thing that is genuinely unobvious
 * about most of these fields is not what to type but WHERE IT GOES — some are
 * read by an FTC mid-call, some are handed to the AI voice agent as its notes,
 * and some configure the transcript extractor. That is what the help says.
 */
function FieldLabel({ children, help }) {
  return (
    <label className="ck-label">
      {children}
      {help ? (
        <Tooltip title={help} placement="topLeft" overlayStyle={{ maxWidth: 340 }} zIndex={DRAWER_TOOLTIP_Z}>
          <InfoCircleOutlined className="ck-help" />
        </Tooltip>
      ) : null}
    </label>
  );
}

/**
 * A titled band that names what the fields under it are FOR.
 *
 * The Knowledge tab holds two genuinely different bodies of knowledge — how to
 * reach the information, and what shape it arrives in — and until they were
 * labelled the form read as one long undifferentiated list. The distinction is
 * the whole design of the feature, so it is the loudest thing on the tab.
 *
 * A rule rather than a box: two nested cards inside an 860px drawer crowd a form
 * that is mostly textareas. The band carries the weight, the fields keep the room.
 */
function SectionHead({ icon, title, blurb }) {
  return (
    <div className="ck-section-head">
      <span className="ck-section-mark" aria-hidden="true" />
      <div>
        <div className="ck-section-title">
          <span className="ck-section-icon">{icon}</span>
          {title}
        </div>
        {blurb ? <div className="ck-section-blurb">{blurb}</div> : null}
      </div>
    </div>
  );
}

// term_lexicon travels as {phrase: {code|category}} but edits far better as rows.
function lexiconRows(map) {
  return Object.keys(map || {}).map(function (phrase) {
    const target = map[phrase] || {};
    return { term: phrase, target: target.code || target.category || "" };
  });
}

function rowsToLexicon(rows) {
  const out = {};
  (rows || []).forEach(function (row) {
    const term = String((row && row.term) || "").trim();
    const target = String((row && row.target) || "").trim();
    if (!term || !target) return;
    // All digits is a procedure code; anything else is a coverage category. The
    // server re-derives this too — this is just so the shape is right on arrival.
    out[term] = /^\d+$/.test(target) ? { code: target } : { category: target };
  });
  return out;
}

/**
 * The words THIS carrier uses, and what they mean.
 *
 * Scoped to the task being edited, like every other field on this tab — so a
 * word that means something different on an estimate can say so, and the General
 * task is where carrier-wide vocabulary belongs. `carrier_lexicon` on the server
 * merges the two with the task winning.
 */
function CarrierWordsEditor({ rows, onChange }) {
  const list = rows || [];

  function update(i, patch) {
    onChange(list.map(function (r, idx) {
      return idx === i ? Object.assign({}, r, patch) : r;
    }));
  }

  return (
    <div className="ck-words">
      {list.map(function (row, i) {
        return (
          <div className="ck-word-row" key={i}>
            <Input
              value={row.term}
              onChange={function (e) { update(i, { term: e.target.value }); }}
              placeholder="What they say — e.g. routine cleaning"
            />
            <span className="ck-word-arrow" aria-hidden="true">→</span>
            <AutoComplete
              value={row.target}
              onChange={function (v) { update(i, { target: v }); }}
              options={COVERAGE_CATEGORIES.map(function (c) { return { value: c }; })}
              placeholder="CDA code or category"
              style={{ width: 200 }}
              getPopupContainer={popupInDrawer}
            />
            <Button
              type="text" danger icon={<DeleteOutlined />}
              onClick={function () {
                onChange(list.filter(function (_r, idx) { return idx !== i; }));
              }}
              aria-label="Remove this word"
            />
          </div>
        );
      })}
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={function () { onChange(list.concat([{ term: "", target: "" }])); }}
        style={{ width: "100%" }}
      >
        Add a word
      </Button>
    </div>
  );
}

/**
 * The shared vocabulary, as reference.
 *
 * Read-mostly and deliberately quiet: it is global data sitting inside a
 * per-carrier form, so it must not look like another field of this carrier's.
 * Shown because knowing "recall exam" is already mapped everywhere is what stops
 * someone adding a redundant carrier override.
 */
function SharedVocabulary() {
  const [terms, setTerms] = useState(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ term: "", target: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async function () {
    const res = await getCarrierLexicon();
    setTerms(((res && res.json) || {}).shared || []);
  }, []);

  useEffect(function () { load(); }, [load]);

  async function add() {
    const term = draft.term.trim();
    const target = draft.target.trim();
    if (!term || !target) {
      message.warning("Give both the phrase and what it means.");
      return;
    }
    setBusy(true);
    const isCode = /^\d+$/.test(target);
    const res = await saveLexiconTerm(
      Object.assign({ term: term }, isCode ? { code: target } : { category: target }));
    setBusy(false);
    if (res.status < 300) {
      setDraft({ term: "", target: "" });
      message.success("Added to the shared vocabulary.");
      load();
    } else {
      message.error((res.json && res.json.detail) || "Couldn't add that.");
    }
  }

  async function remove(term) {
    setBusy(true);
    const res = await deleteLexiconTerm({ term: term });
    setBusy(false);
    if (res.status < 300) load();
    else message.error("Couldn't remove that.");
  }

  const count = (terms || []).length;
  // Only the LIST folds away, and only once it is long enough to bury the row
  // below it. The add control is never hidden: an action behind a disclosure
  // reads as an action that no longer exists.
  const LIST_PREVIEW = 6;
  const collapsible = count > LIST_PREVIEW;
  const visible = collapsible && !open ? terms.slice(0, LIST_PREVIEW) : (terms || []);

  return (
    <div className="ck-shared">
      <div className="ck-shared-head">
        <span className="ck-shared-title">Shared vocabulary</span>
        <Tooltip
          placement="topLeft"
          overlayStyle={{ maxWidth: 340 }} zIndex={DRAWER_TOOLTIP_Z}
          title={"Everyday dental wording that means the same thing at every carrier, so "
                 + "it is written once here rather than repeated on each. A carrier's own "
                 + "entry above wins over this when both match."}
        >
          <InfoCircleOutlined className="ck-help" />
        </Tooltip>
        <span className="ck-shared-scope">every carrier</span>
      </div>
      {/* Saves on click, unlike everything else on this tab, and it changes what
          every other carrier matches. Both said plainly rather than left to be
          discovered. */}
      <p className="ck-muted">
        Common dental wording, written once and matched for every carrier. Changes
        here save immediately.
      </p>

      {terms === null ? (
        <span className="ck-blank">loading…</span>
      ) : count ? (
        <div className="ck-shared-list">
          {visible.map(function (row) {
            return (
              <div className="ck-word-row is-static" key={row.id}>
                <span className="ck-lex-term">{row.term}</span>
                <span className="ck-word-arrow" aria-hidden="true">→</span>
                <span className="ck-lex-target">{row.code || row.category}</span>
                {row.is_verified ? null : (
                  <Tag color="orange" className="ck-tag">unverified</Tag>
                )}
                <Button
                  type="text" size="small" danger icon={<DeleteOutlined />}
                  disabled={busy}
                  onClick={function () { remove(row.term); }}
                  aria-label={`Remove ${row.term}`}
                />
              </div>
            );
          })}
          {collapsible ? (
            <button
              type="button"
              className="ck-shared-toggle"
              onClick={function () { setOpen(!open); }}
            >
              {open ? "Show fewer" : `Show all ${count} terms`}
            </button>
          ) : null}
        </div>
      ) : (
        <span className="ck-blank">nothing shared yet</span>
      )}

      <div className="ck-shared-add">
        <Input
          value={draft.term}
          onChange={function (e) { setDraft(Object.assign({}, draft, { term: e.target.value })); }}
          placeholder="What they say"
          onPressEnter={add}
        />
        <span className="ck-word-arrow" aria-hidden="true">→</span>
        <AutoComplete
          value={draft.target}
          onChange={function (v) { setDraft(Object.assign({}, draft, { target: v })); }}
          options={COVERAGE_CATEGORIES.map(function (c) { return { value: c }; })}
          placeholder="CDA code or category"
          style={{ width: 190 }}
          getPopupContainer={popupInDrawer}
        />
        <Button onClick={add} loading={busy}>Add for all carriers</Button>
      </div>
    </div>
  );
}

function ReportingStyleFields({ value, onChange }) {
  const style = value || {};
  function set(key, v) {
    const next = Object.assign({}, style);
    if (v) next[key] = v; else delete next[key];
    onChange(next);
  }
  const rows = [
    ["granularity", "What they quote", GRANULARITY,
     "The single most useful fact about a carrier for the extractor: whether an "
     + "answer arrives as a code, a spoken name, or only a category. 'Names only' "
     + "is what tells it to look for wording and map it through the lexicon."],
    ["quotes_cda_codes", "Do they give CDA codes?", QUOTES_CODES,
     "Whether a code can be got out of them at all, even by asking. 'No' means "
     + "the lexicon below is the only way this carrier's answers become storable."],
    ["frequency_basis", "Frequency basis", FREQUENCY_BASIS,
     "What a limit like 'twice a year' is counted against. A rolling basis "
     + "measured from the last service date is a different promise from a calendar "
     + "year, and carriers rarely say which they mean."],
    ["benefit_year_start", "Benefit year starts", BENEFIT_YEAR_START,
     "When maximums reset. January for most, but plenty run from the policy "
     + "anniversary — which changes when the remaining maximum you were quoted "
     + "stops being true."],
  ];
  return (
    <div className="ck-style-grid">
      {rows.map(function ([key, label, options, help]) {
        return (
          <div className="ck-style-field" key={key}>
            <FieldLabel help={help}>{label}</FieldLabel>
            <Select
              value={style[key] || ""}
              onChange={function (v) { set(key, v); }}
              options={options}
              style={{ width: "100%" }}
              getPopupContainer={popupInDrawer}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * The typed rules. A row editor rather than raw JSON because the type is a fixed
 * vocabulary and picking from it is what gives the rule its `maps_to` — the field
 * on the breakdown that the rule governs, and the thing that makes a rule usable
 * by the extractor rather than merely readable.
 */
function IntakeRulesEditor({ rules, onChange }) {
  const list = rules || [];

  function update(i, patch) {
    onChange(list.map(function (r, idx) {
      return idx === i ? Object.assign({}, r, patch) : r;
    }));
  }
  function add() {
    onChange(list.concat([{ type: "other", applies_to: "", text: "" }]));
  }
  function remove(i) {
    onChange(list.filter(function (_r, idx) { return idx !== i; }));
  }

  return (
    <div className="ck-rules-editor">
      {list.map(function (rule, i) {
        const scope = Array.isArray(rule.applies_to)
          ? rule.applies_to.join(", ")
          : (rule.applies_to || "");
        return (
          <div className="ck-rule-row" key={i}>
            <Select
              value={rule.type || "other"}
              onChange={function (v) { update(i, { type: v }); }}
              options={RULE_TYPES.map(function (t) {
                return { value: t, label: t.replace(/_/g, " ") };
              })}
              style={{ width: 190 }}
              getPopupContainer={popupInDrawer}
            />
            <Input
              value={scope}
              onChange={function (e) {
                // Comma-separated becomes a list — a shared maximum spans several
                // categories and has to be stored as more than one.
                const raw = e.target.value;
                const parts = raw.split(",").map(function (s) { return s.trim(); }).filter(Boolean);
                update(i, { applies_to: parts.length > 1 ? parts : raw });
              }}
              placeholder="Applies to — a category, code, or blank for the whole plan"
              style={{ width: 230 }}
            />
            <Input
              value={rule.text || ""}
              onChange={function (e) { update(i, { text: e.target.value }); }}
              placeholder="What is true of this carrier"
            />
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={function () { remove(i); }}
              aria-label="Remove this rule"
            />
          </div>
        );
      })}
      <Button type="dashed" icon={<PlusOutlined />} onClick={add} style={{ width: "100%" }}>
        Add a rule
      </Button>
    </div>
  );
}

function PlaybookEditor({ carrier, taskType, onSaved }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(
    async function () {
      setLoading(true);
      const res = await getCarrierPlaybook(carrier.carrier_id, taskType);
      const book = ((res.json && res.json.playbooks) || [])
        .find(function (b) { return b.task_type === taskType; });
      setForm({
        instructions: (book && book.instructions) || "",
        quirks: (book && book.quirks) || "",
        limitations: (book && book.limitations) || "",
        availability: (book && book.availability) || "unknown",
        verification_fields: ((book && book.verification_fields) || []).join(", "),
        // Edited as JSON: the shape is {step, description, digits, wait_seconds}
        // and a bespoke row editor would be a lot of surface for a field only a
        // handful of carriers use.
        ivr_sequence: JSON.stringify((book && book.ivr_sequence) || [], null, 2),
        // Intake. must_ask is edited as one-per-line rather than as a row editor:
        // the entries ARE single lines, and a textarea is the fastest way to write
        // several while reading a report.
        reporting_style: (book && book.reporting_style) || {},
        must_ask: ((book && book.must_ask) || []).join("\n"),
        intake_rules: (book && book.intake_rules) || [],
        // As rows for editing; rebuilt into a map on save. Batched with the rest
        // of the form rather than saved on each keystroke, so vocabulary lands in
        // the same version as the knowledge it belongs to.
        term_lexicon: lexiconRows((book && book.term_lexicon) || {}),
        version: (book && book.version) || 0,
      });
      setLoading(false);
    },
    [carrier.carrier_id, taskType],
  );

  useEffect(function () { load(); }, [load]);

  async function save() {
    let ivr;
    try {
      ivr = JSON.parse(form.ivr_sequence || "[]");
      if (!Array.isArray(ivr)) throw new Error("not a list");
    } catch (e) {
      message.error("The phone menu must be a JSON list. Fix it or set it to [].");
      return;
    }
    setSaving(true);
    const res = await saveCarrierPlaybook(carrier.carrier_id, taskType, {
      instructions: form.instructions,
      quirks: form.quirks,
      limitations: form.limitations,
      availability: form.availability,
      verification_fields: form.verification_fields
        .split(",").map(function (v) { return v.trim(); }).filter(Boolean),
      ivr_sequence: ivr,
      reporting_style: form.reporting_style || {},
      must_ask: (form.must_ask || "")
        .split("\n").map(function (v) { return v.trim(); }).filter(Boolean),
      // Sent as-is; the server normalises (drops textless rules, coerces an
      // unknown type to "other", fills maps_to from the taxonomy) so the editor
      // and the LLM merge cannot store two different shapes.
      intake_rules: (form.intake_rules || []).filter(function (r) {
        return r && String(r.text || "").trim();
      }),
      term_lexicon: rowsToLexicon(form.term_lexicon),
      change_summary: "Edited in Carrier Knowledge.",
    });
    setSaving(false);
    if (res.status < 300) {
      message.success("Saved as v" + (res.json && res.json.version) + ".");
      load();
      if (onSaved) onSaved();
    } else {
      message.error((res.json && res.json.detail) || "Couldn't save that.");
    }
  }

  if (loading || !form) return <div className="ck-loading">Loading…</div>;

  return (
    <div className="ck-editor">
      <div className="ck-section">
        <SectionHead
          icon={<NodeIndexOutlined />}
          title="How to get it"
          blurb="Reaching a person, or a page, that will answer."
        />

        <FieldLabel help="The main how-to. For a portal, where to log in and where to click; for a phone, what to say. Shown to FTC staff on the carrier card when they open a task, and handed to the AI voice agent as its carrier notes.">
          Instructions — what to actually do
        </FieldLabel>
        <Input.TextArea
          value={form.instructions}
          onChange={function (e) { setForm(Object.assign({}, form, { instructions: e.target.value })); }}
          autoSize={{ minRows: 4, maxRows: 14 }}
          placeholder="For a portal: where to log in and where to click. For a phone: what to say."
        />

        <FieldLabel help="What this route CANNOT tell you, and what to do instead. Kept separate from the instructions because it is read before starting — 'the portal shows the annual maximum but never frequency limits, call for those' saves the whole attempt.">
          What it won't give you
        </FieldLabel>
        <Input.TextArea
          value={form.limitations}
          onChange={function (e) { setForm(Object.assign({}, form, { limitations: e.target.value })); }}
          autoSize={{ minRows: 2, maxRows: 6 }}
          placeholder="e.g. The portal shows the annual max but never frequency limits — call for those."
        />

        <FieldLabel help="Whether this task is possible for this carrier at all. 'No known route' is knowledge worth recording, not an absence — it is what stops the next person repeating the search.">
          Can this be done?
        </FieldLabel>
        <Select
          value={form.availability}
          onChange={function (v) { setForm(Object.assign({}, form, { availability: v })); }}
          options={AVAILABILITY}
          style={{ width: "100%" }}
          getPopupContainer={popupInDrawer}
        />

        <FieldLabel help="What the rep demands before they will talk, e.g. member_id, date_of_birth, provider_number. The voice agent uses this to have the answers ready rather than being stopped by the question.">
          They'll ask for — comma separated
        </FieldLabel>
        <Input
          value={form.verification_fields}
          onChange={function (e) { setForm(Object.assign({}, form, { verification_fields: e.target.value })); }}
          placeholder="member_id, date_of_birth, provider_number"
        />

        <FieldLabel help={"The phone menu, as [{step, description, digits, wait_seconds}]. This is what the voice agent dials by, so a wrong digit costs a real call. Portal navigation does NOT belong here — write it as prose in Instructions, because portals get relaid out and a rigid step list goes stale."}>
          Phone menu path — JSON, empty for portal carriers
        </FieldLabel>
        <Input.TextArea
          className="ck-mono"
          value={form.ivr_sequence}
          onChange={function (e) { setForm(Object.assign({}, form, { ivr_sequence: e.target.value })); }}
          autoSize={{ minRows: 3, maxRows: 12 }}
        />

        <FieldLabel help="The 'and by the way' knowledge that fits nowhere else — 'they never answer before 10am', 'ask for the dental desk, not benefits'. Also appended to the voice agent's notes.">
          Quirks
        </FieldLabel>
        <Input.TextArea
          value={form.quirks}
          onChange={function (e) { setForm(Object.assign({}, form, { quirks: e.target.value })); }}
          autoSize={{ minRows: 2, maxRows: 6 }}
        />
      </div>

      {/* The second half answers a different question from the first: not "how do
          I reach the information" but "what shape does it arrive in". That
          distinction is the whole design of the feature — and this half is what
          configures the transcript extractor — so it is labelled as loudly as the
          first rather than trailing off the bottom of one long form. */}
      <div className="ck-section">
        <SectionHead
          icon={<MessageOutlined />}
          title="How this carrier reports coverage"
          blurb="What the answer looks like when it arrives, and what you have to ask for."
        />

        <p className="ck-muted ck-section-caveat">
          Carrier conventions only. A patient's plan value ("this plan allows 2 per
          year") belongs on their breakdown — recorded here it would be wrong for
          everyone else.
        </p>

        <ReportingStyleFields
          value={form.reporting_style}
          onChange={function (next) { setForm(Object.assign({}, form, { reporting_style: next })); }}
        />

        <FieldLabel help={"Things this carrier will not offer unless asked. Doing double duty: a checklist the FTC reads before dialling, and the list of slots the transcript extractor tries to fill. One short imperative line each."}>
          They won't volunteer these — one per line
        </FieldLabel>
        <Input.TextArea
          value={form.must_ask}
          onChange={function (e) { setForm(Object.assign({}, form, { must_ask: e.target.value })); }}
          autoSize={{ minRows: 2, maxRows: 8 }}
          placeholder={"Ask for the procedure code, they only read the name\n"
                       + "Ask whether the recall frequency is rolling or calendar"}
        />

        <FieldLabel help={"Carrier conventions that change how a number should be READ — shared maximums, age caps, rolling vs calendar frequency. Picking a type is what links the rule to the breakdown field it governs, so the extractor can apply it and flag a call that contradicts it."}>
          Rules that qualify what they tell you
        </FieldLabel>
        <IntakeRulesEditor
          rules={form.intake_rules}
          onChange={function (next) { setForm(Object.assign({}, form, { intake_rules: next })); }}
        />

        {/* Vocabulary belongs to "how they report coverage": a carrier that says
            "routine cleaning" instead of a code is describing the shape of its
            answers, and mapping the phrase is what makes that answer storable. */}
        <div className="ck-subsection">
          <div className="ck-subsection-head">
            <TranslationOutlined className="ck-subsection-icon" />
            <span className="ck-subsection-title">What their words mean</span>
            <Tooltip
              placement="topLeft"
              overlayStyle={{ maxWidth: 340 }} zIndex={DRAWER_TOOLTIP_Z}
              title={"Maps a phrase this carrier uses onto a procedure code or a coverage "
                     + "category. Without an entry, a rep who only says \"routine cleaning\" "
                     + "produces no coverage row at all — the extractor keys on the code."}
            >
              <InfoCircleOutlined className="ck-help" />
            </Tooltip>
            <span className="ck-subsection-scope">
              {taskType === "general" ? "every task" : `${taskType} tasks`}
            </span>
          </div>
          <p className="ck-muted">
            Only what is peculiar to this carrier. Everyday dental wording lives in
            the shared vocabulary below.
            {taskType === "general"
              ? " Written here, it applies to all of this carrier's tasks."
              : " For wording that holds across every task, use the General task."}
          </p>
          <CarrierWordsEditor
            rows={form.term_lexicon}
            onChange={function (next) { setForm(Object.assign({}, form, { term_lexicon: next })); }}
          />
          <SharedVocabulary />
        </div>
      </div>

      <div className="ck-editor-foot">
        <span className="ck-muted">Currently v{form.version}</span>
        <Button type="primary" loading={saving} onClick={save}>Save as a new version</Button>
      </div>
    </div>
  );
}

// --- history ----------------------------------------------------------------

function PlaybookHistory({ carrier, taskType, onReverted }) {
  const [rows, setRows] = useState([]);
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    async function () {
      const res = await getCarrierPlaybookHistory(carrier.carrier_id, taskType);
      setRows((res.json && res.json.revisions) || []);
      setCurrent(res.json && res.json.current_version);
    },
    [carrier.carrier_id, taskType],
  );

  useEffect(function () { load(); }, [load]);

  async function revert(version) {
    setBusy(true);
    const res = await revertCarrierPlaybook(carrier.carrier_id, taskType, version);
    setBusy(false);
    if (res.status < 300) {
      message.success("Reverted to v" + version + " — recorded as a new version.");
      load();
      if (onReverted) onReverted();
    } else {
      message.error((res.json && res.json.detail) || "Couldn't revert.");
    }
  }

  if (!rows.length) return <Empty description="No versions yet" />;

  return (
    <div className="ck-history">
      {rows.map(function (r) {
        return (
          <div className={"ck-rev" + (r.version === current ? " is-current" : "")} key={r.version}>
            <div className="ck-rev-head">
              <span className="ck-rev-v">v{r.version}{r.version === current ? " · current" : ""}</span>
              <Tag className="ck-tag">{r.source_label}</Tag>
              <span className="ck-muted">{r.by || "system"} · {when(r.at)}</span>
              {r.version !== current ? (
                <Button size="small" disabled={busy} onClick={function () { revert(r.version); }}>
                  Revert to this
                </Button>
              ) : null}
            </div>
            <div className="ck-rev-summary">{r.change_summary || "—"}</div>
            {/* The sentence a human actually wrote, kept verbatim. The playbook is
                a summary of these; when the summary looks wrong, this is the
                evidence. */}
            {r.report_text ? (
              <div className="ck-rev-report">“{r.report_text}”</div>
            ) : null}
            {r.instructions ? (
              <details className="ck-rev-detail">
                <summary>Text at this version</summary>
                <pre>{r.instructions}</pre>
                {r.limitations ? <pre className="ck-rev-limits">{r.limitations}</pre> : null}
              </details>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// --- reports ----------------------------------------------------------------

function ReportsList({ carrierId, deferredOnly, onChanged }) {
  const [rows, setRows] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(
    async function () {
      const res = await getCarrierKnowledgeReports({
        carrierId: carrierId,
        status: deferredOnly ? "deferred" : undefined,
      });
      setRows((res.json && res.json.reports) || []);
    },
    [carrierId, deferredOnly],
  );

  useEffect(function () { load(); }, [load]);

  async function retry(id) {
    setBusyId(id);
    const res = await retryCarrierReport(id);
    setBusyId(null);
    if (res.status < 300) {
      message.success(res.json && res.json.applied
        ? "Applied as v" + res.json.version + "."
        : "Ran, but nothing needed changing.");
      load();
      if (onChanged) onChanged();
    } else {
      message.error("Still couldn't apply it.");
    }
  }

  if (!rows.length) return <Empty description={deferredOnly ? "Nothing stuck" : "No reports yet"} />;

  return (
    <div className="ck-reports">
      {rows.map(function (r) {
        return (
          <div className="ck-report" key={r.id}>
            <div className="ck-report-head">
              <strong>{r.carrier_name}</strong>
              <Tag className="ck-tag">{r.task_type}</Tag>
              {r.worked === true ? <Tag color="green" className="ck-tag">worked</Tag> : null}
              {r.worked === false ? <Tag color="orange" className="ck-tag">correction</Tag> : null}
              <span className="ck-muted">{r.by || "—"} · {when(r.at)}</span>
              {!r.processed_at ? (
                <Button size="small" icon={<RedoOutlined />} loading={busyId === r.id}
                        onClick={function () { retry(r.id); }}>
                  Retry
                </Button>
              ) : null}
            </div>
            <div className="ck-report-text">“{r.what_happened || "(no detail)"}”</div>
            <div className="ck-muted">
              {r.processed_at
                ? (r.resulting_version
                    ? `Applied as v${r.resulting_version} — ${r.change_summary}`
                    : "Considered; nothing needed changing.")
                : `Not applied yet${r.process_error ? " — " + r.process_error.slice(0, 120) : ""}`}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * The task picker, repeated on every tab it actually governs.
 *
 * Playbooks are per (carrier, task); credentials, procedure codes and the carrier
 * record are not. A single control at the top of the drawer would silently apply
 * to half the tabs, which is worse than showing it twice.
 */
function TaskPicker({ value, onChange }) {
  return (
    <div className="ck-taskpicker">
      <Select
        value={value}
        onChange={onChange}
        options={TASKS.map(function (t) { return { value: t.key, label: t.label }; })}
        style={{ width: 220 }}
        getPopupContainer={popupInDrawer}
      />
      <Tooltip
        placement="right"
        overlayStyle={{ maxWidth: 340 }} zIndex={DRAWER_TOOLTIP_Z}
        title={"Everything below belongs to this task. Getting a breakdown and getting an "
               + "estimate are different jobs at most carriers, so they hold separate "
               + "knowledge. Use General for what is true of the carrier whatever you are "
               + "doing — it is shown alongside the task's own guidance, not instead of it."}
      >
        <InfoCircleOutlined className="ck-help" />
      </Tooltip>
    </div>
  );
}

/** Create a carrier. Code is immutable afterwards, so it is asked for once. */
function AddCarrierModal({ open, onClose, onCreated }) {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(function () {
    if (open) { setCode(""); setName(""); setActive(true); }
  }, [open]);

  async function create() {
    if (!code.trim() || !name.trim()) {
      message.error("A carrier needs both a code and a name.");
      return;
    }
    setSaving(true);
    const res = await controller.createInsuranceProvider({
      code: code.trim(), name: name.trim(), is_active: active,
    });
    setSaving(false);
    if (res && res._httpStatus != null && res._httpStatus >= 400) {
      message.error((res && res.detail) || "Couldn't create that carrier.");
      return;
    }
    message.success("Carrier created.");
    onClose();
    if (onCreated) onCreated();
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={create}
      confirmLoading={saving}
      okText="Create carrier"
      title="Add a carrier"
      destroyOnClose
      width={480}
    >
      <label className="ck-label">Code</label>
      <Input
        value={code}
        onChange={function (e) { setCode(e.target.value); }}
        placeholder="e.g. SUNLIFE"
      />
      <p className="ck-muted">
        Stored on every policy and used by integrations. It can't be changed later.
      </p>

      <label className="ck-label">Display name</label>
      <Input
        value={name}
        onChange={function (e) { setName(e.target.value); }}
        placeholder="Carrier display name"
      />

      <div className="ck-record-toggle">
        <Switch checked={active} onChange={setActive} />
        <span>{active ? "Active — offered when adding a policy" : "Inactive"}</span>
      </div>
    </Modal>
  );
}

// --- the page ---------------------------------------------------------------

export default function CarrierKnowledgePage() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  // Requests moved in here: every one today is a carrier_contact suggestion
  // raised from the carrier bar, so this is where someone already looking at
  // carrier facts expects it. See the note in RequestsPanel.
  const [tab, setTab] = useState("carriers");
  const [open, setOpen] = useState(null);        // the carrier in the drawer
  const [task, setTask] = useState("breakdown");
  const [showDeferred, setShowDeferred] = useState(false);
  const [adding, setAdding] = useState(false);
  // Shared by the tabs that are per-office, so picking a practice once carries
  // between credentials and procedure codes.
  const [officeId, setOfficeId] = useState(null);
  const [offices, setOffices] = useState([]);
  const [capabilities, setCapabilities] = useState(null);

  const load = useCallback(async function () {
    setLoading(true);
    const res = await getCarrierKnowledgeOverview();
    setRows((res.json && res.json.carriers) || []);
    setCounts((res.json && res.json.counts) || {});
    setLoading(false);
  }, []);

  useEffect(function () { load(); }, [load]);

  // Fetched once for the whole page: both are static lists the drawer tabs read,
  // and refetching them per carrier would be noise.
  useEffect(function () {
    (async function () {
      const res = await getOfficesList();
      setOffices((res && res.json) || []);
    })();
    (async function () {
      const matrix = await controller.getCarrierCapabilities();
      if (matrix && (matrix._httpStatus == null || matrix._httpStatus < 400)) {
        setCapabilities(matrix);
      }
    })();
  }, []);

  const filtered = useMemo(function () {
    const q = search.trim().toLowerCase();
    const list = q
      ? rows.filter(function (r) {
          return (r.carrier_name || "").toLowerCase().indexOf(q) !== -1
            || (r.carrier_code || "").toLowerCase().indexOf(q) !== -1;
        })
      : rows.slice();
    // Office-added carriers waiting for review first, then unwritten ones: the
    // gaps are what this page is for.
    list.sort(function (a, b) {
      if (!!a.needs_review !== !!b.needs_review) return a.needs_review ? -1 : 1;
      if (a.written !== b.written) return a.written ? 1 : -1;
      return (a.carrier_name || "").localeCompare(b.carrier_name || "");
    });
    return list;
  }, [rows, search]);

  const columns = [
    {
      title: "Carrier",
      key: "carrier",
      width: "26%",
      render: function (_, r) {
        return (
          <div>
            <div className="ck-carrier">
              {r.carrier_name}
              {r.needs_review ? (
                <Tooltip title={"Added by " + (r.added_from_office_name || "an office") + " — not reviewed yet"}>
                  <Tag color="orange" style={{ marginLeft: 8 }}>Office-added</Tag>
                </Tooltip>
              ) : null}
            </div>
            <div className="ck-muted">{r.carrier_code}</div>
          </div>
        );
      },
    },
    {
      title: "How it's done",
      key: "methods",
      width: "20%",
      render: function (_, r) {
        return (
          <div className="ck-muted">
            <div>Breakdowns: {r.breakdown_method || "unknown"}</div>
            <div>Estimates: {r.estimate_method || "unknown"}</div>
          </div>
        );
      },
    },
    ...TASKS.map(function (t) {
      return {
        title: t.label,
        key: t.key,
        width: "13%",
        render: function (_, r) { return <TaskCell info={r.tasks[t.key]} />; },
      };
    }),
    {
      title: "",
      key: "action",
      width: "10%",
      align: "right",
      render: function (_, r) {
        return (
          <Button size="small" icon={<EditOutlined />}
                  onClick={function () { setOpen(r); setTask("breakdown"); setOfficeId(null); }}>
            Open
          </Button>
        );
      },
    },
  ];

  return (
    <DashboardLayout>
      <div className="crm-page ck-page">
        <div className="crm-header">
          <div>
            <h1 className="crm-title"><BulbOutlined /> Carrier Knowledge</h1>
            <p className="crm-subtitle">
              What FTC staff see on the carrier card when they open a task. Anyone
              can correct it from there; this is where it is curated.
            </p>
          </div>
          {tab === "carriers" ? (
            <div className="ck-header-actions">
              <Button icon={<PlusOutlined />} type="primary" onClick={function () { setAdding(true); }}>
                Add carrier
              </Button>
              <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
            </div>
          ) : null}
        </div>

        <div className="ck-tabs">
          {[
            { key: "carriers", label: "Carriers" },
            {
              key: "pms",
              label: "PMS carriers" + (counts.unresolved_pms_carriers ? " (" + counts.unresolved_pms_carriers + ")" : ""),
            },
            { key: "requests", label: "Requests" },
          ].map(
            function (t) {
              return (
                <button
                  key={t.key}
                  type="button"
                  className={"ck-tab" + (tab === t.key ? " is-active" : "")}
                  onClick={function () { setTab(t.key); }}
                >
                  {t.label}
                </button>
              );
            },
          )}
        </div>

        {tab === "requests" ? <RequestsPanel /> : null}
        {tab === "pms" ? <UnresolvedPmsCarriersPanel carriers={rows} onChanged={load} /> : null}

        {tab === "carriers" && counts.deferred_reports ? (
          <Alert
            className="ck-alert"
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            message={
              counts.deferred_reports === 1
                ? "1 report hasn't been applied"
                : counts.deferred_reports + " reports haven't been applied"
            }
            description="Someone contributed what they learned and it never reached the guidance. Nothing else will retry these."
            action={
              <Button size="small" onClick={function () { setShowDeferred(true); }}>
                Review
              </Button>
            }
          />
        ) : null}

        <div className="ck-toolbar" style={{ display: tab === "carriers" ? undefined : "none" }}>
          <span className="ck-counts">
            <strong>{counts.written || 0}</strong> written ·{" "}
            <strong>{counts.blank || 0}</strong> with nothing written ·{" "}
            {counts.needs_review ? <><strong>{counts.needs_review}</strong> added by offices to review · </> : null}
            {counts.total || 0} carriers
          </span>
          <Input.Search
            placeholder="Find a carrier"
            allowClear
            value={search}
            onChange={function (e) { setSearch(e.target.value); }}
            style={{ width: 260 }}
          />
        </div>

        <div className="crm-table-card"
             style={{ display: tab === "carriers" ? undefined : "none" }}>
          <Table
            rowKey="carrier_id"
            loading={loading}
            columns={columns}
            dataSource={filtered}
            pagination={false}
            tableLayout="fixed"
          />
        </div>
      </div>

      <Drawer
        open={!!open}
        onClose={function () { setOpen(null); }}
        width={860}
        title={open ? open.carrier_name : ""}
        destroyOnClose
      >
        {open ? (
          <Tabs
            items={[
              {
                key: "knowledge",
                label: <span><BulbOutlined /> Knowledge</span>,
                children: (
                  <>
                    <TaskPicker value={task} onChange={setTask} />
                    <PlaybookEditor carrier={open} taskType={task} onSaved={load} />
                  </>
                ),
              },
              {
                key: "history",
                label: <span><HistoryOutlined /> History</span>,
                children: (
                  <>
                    <TaskPicker value={task} onChange={setTask} />
                    <PlaybookHistory carrier={open} taskType={task} onReverted={load} />
                  </>
                ),
              },
              {
                key: "reports",
                label: "Reports",
                children: <ReportsList carrierId={open.carrier_id} onChanged={load} />,
              },
              {
                key: "credentials",
                label: <span><KeyOutlined /> Credentials</span>,
                children: (
                  <CredentialsTab
                    carrier={open}
                    officeId={officeId}
                    setOfficeId={setOfficeId}
                  />
                ),
              },
              {
                key: "codes",
                label: <span><ProfileOutlined /> Procedure codes</span>,
                children: (
                  <ProcedureCodesTab
                    carrier={open}
                    officeId={officeId}
                    setOfficeId={setOfficeId}
                    offices={offices}
                  />
                ),
              },
              {
                key: "names",
                label: <span><TranslationOutlined /> Names{open.needs_review ? " ·" : ""}</span>,
                children: <CarrierNamesTab carrier={open} onChanged={load} />,
              },
              {
                key: "record",
                label: <span><ApartmentOutlined /> Carrier</span>,
                children: (
                  <CarrierRecordTab
                    carrier={open}
                    capabilities={capabilities}
                    onSaved={load}
                    onDeleted={function () { setOpen(null); load(); }}
                  />
                ),
              },
            ]}
          />
        ) : null}
      </Drawer>

      <AddCarrierModal
        open={adding}
        onClose={function () { setAdding(false); }}
        onCreated={load}
      />

      <Drawer
        open={showDeferred}
        onClose={function () { setShowDeferred(false); }}
        width={640}
        title="Reports that never reached the guidance"
        destroyOnClose
      >
        <ReportsList deferredOnly onChanged={load} />
      </Drawer>
    </DashboardLayout>
  );
}
